import { DatePicker, FileInput, Input, Select, VersionFileInput } from '@/components/Form'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { OfferSourcingDocumentTypes } from '@/features/offers/constants'
import { authSelector } from '@/stores/auth/authSlice'
import { readExcelFile } from '@/utils/fileHelper'
import { isNullish } from '@/utils/helper'
import { SimpleGrid } from '@chakra-ui/react'
import { GCPRLicenseType } from '@eapproval/offer-shared'
import { useEffect, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

const LICENSE_TYPE_OPTIONS = Object.values(GCPRLicenseType).map((v) => ({ value: v, label: v }))

const GCPRSubmissionFormFields = ({ submissionIndex, canEditGCPRSubmission, miniumDeliveryDate, showCSIDFields }) => {
  const { user } = useSelector(authSelector)
  const { setValue } = useFormContext()
  const licenseType = useWatch({
    name: `sourcing.GCMaterialSubmissions.${submissionIndex}.licenseType`,
  })
  const previousLicenseTypeRef = useRef(licenseType)

  const isCPM = user?.contributorGroup === ContributorGroupType.COMMERCIAL_PROJECT_MANAGER

  useEffect(() => {
    const previousLicenseType = previousLicenseTypeRef.current
    if (previousLicenseType !== licenseType) {
      const basePath = `sourcing.GCMaterialSubmissions.${submissionIndex}`
      setValue(`${basePath}.licenseSiteAddress`, '', { shouldDirty: true, shouldTouch: true })
      setValue(`${basePath}.licenseEmailAddress`, '', { shouldDirty: true, shouldTouch: true })
      setValue(`${basePath}.licenseCsid`, '', { shouldDirty: true, shouldTouch: true })
      setValue(`${basePath}.licenseExistingDongleNumber`, '', { shouldDirty: true, shouldTouch: true })
      setValue(`${basePath}.licenseFile`, [], { shouldDirty: true, shouldTouch: true })
    }

    previousLicenseTypeRef.current = licenseType
  }, [licenseType, setValue, submissionIndex])

  const handleUploadBuyNow = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      toast.info('Buy Now file uploaded')
      const fileStream = await file.arrayBuffer()
      const parsedData = readExcelFile(fileStream)

      if (Array.isArray(parsedData)) {
        parsedData.forEach((row, rowIndex) => {
          if (row['Purchase Scenario (eApproval)']) {
            setValue(
              `sourcing.GCMaterialSubmissions.${submissionIndex}.requestedDetails.${rowIndex}.purchaseScenario`,
              row['Purchase Scenario (eApproval)'],
              { shouldDirty: true }
            )
          }
        })
      }
      console.log('parsedData', parsedData)
    } catch (err) {
      toast.error('Error in uploading file: ' + err.message)
    }
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="3" my="3">
        <Input
          name={`sourcing.GCMaterialSubmissions.${submissionIndex}.descriptionOfProduct`}
          label="Remark"
          type="text"
          disabled={!canEditGCPRSubmission}
          isRequired={false}
          isRow
        />

        <DatePicker
          name={`sourcing.GCMaterialSubmissions.${submissionIndex}.expectedDeliveryDate`}
          label="Expected Delivery Date"
          type="text"
          disabled={!canEditGCPRSubmission}
          isRequired={true}
          isRow
          minDate={miniumDeliveryDate}
        />
        <Input
          name={`sourcing.GCMaterialSubmissions.${submissionIndex}.deliveryInstructions`}
          label="Comment"
          type="text"
          disabled={!canEditGCPRSubmission}
          isRequired={false}
          isRow
        />
        <FileInput
          label="Filled Buy Now"
          name={`sourcing.GCMaterialSubmissions.${submissionIndex}.filledBuyNow`}
          canUpload={canEditGCPRSubmission}
          isRequired={false}
          isRow
          fileType={OfferSourcingDocumentTypes.SOURCING_GCPR_BUYNOW}
          extraOnUpload={handleUploadBuyNow}
        />
        <Input
          name={`sourcing.GCMaterialSubmissions.${submissionIndex}.SONumber`}
          label="SO# / SAP PR#"
          type="text"
          disabled={!canEditGCPRSubmission}
          isRequired={isCPM}
          isRow
        />
        <VersionFileInput
          name={`sourcing.GCMaterialSubmissions.${submissionIndex}.internalDocument`}
          label="Internal Document"
          fileType={OfferSourcingDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
          canUpload={canEditGCPRSubmission}
          isRequired={false}
          isMultiple
          isRow
        />
      </SimpleGrid>
      {showCSIDFields && (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap="3" my="3" borderTop={'1px solid'} pt="4" borderColor={'gray.200'}>
          <Select
            name={`sourcing.GCMaterialSubmissions.${submissionIndex}.licenseType`}
            label="License Type"
            options={LICENSE_TYPE_OPTIONS}
            disabled={!canEditGCPRSubmission}
            isRequired={true}
            isRow
          />

          {licenseType === 'New' && (
            <>
              <Input
                name={`sourcing.GCMaterialSubmissions.${submissionIndex}.licenseSiteAddress`}
                label="Site Address"
                type="text"
                disabled={!canEditGCPRSubmission}
                isRequired={true}
                isRow
              />
              <Input
                name={`sourcing.GCMaterialSubmissions.${submissionIndex}.licenseEmailAddress`}
                label="Email Address"
                type="text"
                disabled={!canEditGCPRSubmission}
                isRequired={true}
                isRow
              />
            </>
          )}

          {(licenseType === GCPRLicenseType.MIGRATION ||
            licenseType === GCPRLicenseType.RENEW ||
            licenseType === GCPRLicenseType.CHANGE_OF_SCOPE) && (
            <>
              <Input
                name={`sourcing.GCMaterialSubmissions.${submissionIndex}.licenseCsid`}
                label="CSID (Only 1 CSID per PR)"
                type="text"
                disabled={!canEditGCPRSubmission}
                isRequired={true}
                isRow
              />
              <Input
                name={`sourcing.GCMaterialSubmissions.${submissionIndex}.licenseExistingDongleNumber`}
                label="Existing Dongle Number"
                type="text"
                disabled={!canEditGCPRSubmission}
                isRequired={true}
                isRow
              />
            </>
          )}

          {!isNullish(licenseType) && (
            <VersionFileInput
              name={`sourcing.GCMaterialSubmissions.${submissionIndex}.licenseFile`}
              label="License File"
              fileType={OfferSourcingDocumentTypes.SOURCING_GCPR_LICENSE_FILE}
              canUpload={canEditGCPRSubmission}
              isRequired={!isNullish(licenseType)}
              isMultiple
              isRow
            />
          )}
        </SimpleGrid>
      )}
    </>
  )
}

export default GCPRSubmissionFormFields
